import { useEffect, useState } from 'react'

// material-ui
import { Button, Chip, MenuItem, Box, Typography, Menu, Dialog, DialogContent, DialogActions, Alert, Stack, IconButton, Tooltip  } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles'
import { PlusCircleOutlined, EyeOutlined, CloseCircleOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from '@ant-design/icons'

// project import
import { useAuth } from 'services/hook/Auth'
import { useApiData } from 'services/hook/useApiData'
import { useAuthContext } from 'services/provider/AuthProvider';
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { PageHeader } from 'components/PageHeader'
import CellRenderer from 'components/list/CellRenderer';
import { getPermissionKeys } from 'utils/util';
import { orderList, orderToggleStatus, generateUrl } from '../../models/order';
import { EVENT_ORDER_DELETE_INIT, EVENT_ORDER_UPDATE_INIT, EVENT_ORDER_UPDATE_SUCCESS } from '../../models/events/order.event';
import { CellMobileDetails } from './list/Order.List.Cell';
import { OrderListFilter } from './list/Order.List.Filter';
import DataGridList from '../../components/data-grid/DataGridList';
import { formatPhoneNumber } from 'utils/helpers';
import { useNavigate } from 'react-router';
import { AccessTimeOutlined, CheckCircleOutlineRounded, DownloadOutlined, InfoOutlined, LocalShippingOutlined, SyncOutlined, WarningOutlined } from '@mui/icons-material';
import DirhamIcon from '../../assets/images/dirham-symbol.png'
import { parseTimeRange, to12Hour } from '../../utils/timeUtils';
import { useSnackbar } from 'notistack';
import { exportDownloadUrl, exportInit } from '../../models/orderExport';
import OrderPrintDialog from '../../components/print/OrderPrintDialog';


/**
 * List order page base component
 */
const OrderList = () => {
    const permissionsKeys = getPermissionKeys('order');
    return (<>
        <PageHeader title="Orders" breadcrumbs={[{ path: '#', label: 'Orders' }]} />
        <OrderListTable permissionsKeys={permissionsKeys} />
        {/* {permissionsKeys.can_create ? <OrderAddDialog /> : null}
        {permissionsKeys.can_delete ? <OrderDeleteDialog /> : null} */}
    </>)
}

/**
 * List all orders in data grid
 */
const OrderListTable = ({ permissionsKeys }) => {
    const { signOut } = useAuthContext();
    const eventbus = useEventBusContext();
    const is_mobile = useMediaQuery('(max-width:991px)');
    const initdata = { data: [], total: 0 }
    const initparams = { page: 0, limit: 10, sort: '', orderby: '', filters: {} }

    const colheadsx = {
        '& .MuiDataGrid-cellCheckbox' : { alignItems: 'center' },
        '& .MuiDataGrid-topContainer': { opacity: 0, visibility: 'hidden' },
        '& .MuiDataGrid-cell': { 
            borderBottom: '1px solid #c5c5c5a1 !important',
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const desktopGridSx = {
        '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            lineHeight: 1.5,
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-row': {
            minHeight: '48px !important',
            borderBottom: '1px solid #e0e0e0 !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-cellContent': {
            fontSize: '0.875rem'
        },
        '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f5f5f5',
            fontSize: '1rem', 
            fontWeight: 600,
            outline: 'none !important', 
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '1rem', 
            fontWeight: 600,
            outline: 'none !important', 
            '&:focus': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
            outline: 'none !important', 
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }
    
    const [data, params, loading, loadData] = useApiData(
        initdata,
        initparams,
        (params, setData) => {
            orderList({ ...params, ...params.filters })
            .then((res) => {
                setData(res)
            })
            .catch((res) => {
                console.log(res)
            })
        }, false
    )
    

    useEffect(() => {
        return eventbus.on(EVENT_ORDER_UPDATE_SUCCESS, () => {
            loadData(params)
        })
    }, [eventbus, params, loadData])

    return (
        <>
            <DataGridList
                rows={data.data}
                columns={!is_mobile ? columns : mobile_cols}
                getRowHeight={() => 'auto'}
                actions={!is_mobile ? <ListActions params={params} /> : <ListActionMenuItems />}
                loading={loading}
                filterkey='orderlist'       
                sx={!is_mobile ? desktopGridSx : colheadsx}
                checkboxSelection={!is_mobile ? false : false}
                columnHeaderHeight={!is_mobile ? 56 : 0} 
                filter={(filters, setFilters) => (<OrderListFilter filters={filters} setFilters={setFilters} />)}
                onFilter={(values) => {
                    params['filters'] = values
                    loadData(params)
                }}
                search={(data) => {
                    loadData({ ...params, query: data })
                }}
                rowCount={data.total}
                paginationMode="server"
                paginationModel={{ page: params.page, pageSize: params.limit }}
                onPaginationModelChange={(pagination) => {
                    if (pagination.page === params.page && pagination.pageSize === params.limit) {
                        return;
                    }
                    loadData({ ...params, page: pagination.page, limit: pagination.pageSize })
                }}
                sortingMode="server"
                onSortModelChange={(sortModel) => {
                    if (sortModel.length > 0) {
                        loadData({ ...params, orderby: sortModel[0].field, sort: sortModel[0].sort })
                    } else {
                        loadData({ ...params, orderby: '', sort: '' })
                    }
                }}
                onInit={(pagination, sortModel, seach, filters) => {
                    var inparam = { query: seach, filters: filters }
                    if (pagination != undefined) {
                        inparam = { ...inparam, page: pagination.page, limit: pagination.pageSize }
                    }
                    if (sortModel != undefined && sortModel.length > 0) {
                        inparam = { ...inparam, orderby: sortModel[0].field, sort: sortModel[0].sort }
                    }

                    loadData({ ...params, ...inparam })
                }}
            />
        </>
    )
}

//add list page global actions
// const ListActions = () => {
//     const eventbus = useEventBusContext()
//     const { checkPermission } = useAuth()
//     const navigate = useNavigate();

//     return (<>
//         {checkPermission('order.create') ? <AdminOrderButton /> : null}
//     </>)
// }

const ListActions = ({ params = {} }) => {
    const { checkPermission } = useAuth()
    const [anchorEl, setAnchorEl] = useState(null)
    const [exporting, setExporting] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const open = Boolean(anchorEl)

    const handleExport = async (format) => {
        setAnchorEl(null)
        try {
            setExporting(true)

            const activeFilters = {
                ...(params.filters || {}),
                query: params.query    || undefined,
                sort: params.sort     || undefined,
                orderby: params.orderby  || undefined,
            }

            const res = await exportInit({
                format,
                type:    'all',
                filters: activeFilters,  // ← send filters
            })

            const key = res?.key || res?.data?.key
            if (!key) throw new Error('No export key received')

            window.open(exportDownloadUrl(key), '_blank', 'noopener,noreferrer')
            enqueueSnackbar(`${format.toUpperCase()} export started`, { variant: 'success', autoHideDuration: 2000 })
        } catch (err) {
            enqueueSnackbar(err?.message || 'Export failed', { variant: 'error', autoHideDuration: 2500 })
        } finally {
            setExporting(false)
        }
    }

    return (
        <Stack direction="row" spacing={1}>
            {checkPermission('order.create') && <AdminOrderButton />}

            <Button
                variant="outlined"
                color="primary"
                disabled={exporting}
                endIcon={<DownloadOutlined />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {exporting ? 'Exporting...' : 'Export'}
            </Button>

            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => handleExport('excel')}>
                    <FileExcelOutlined style={{ color: '#389e0d', marginRight: 8 }} />
                    Export as Excel
                </MenuItem>
                <MenuItem onClick={() => handleExport('pdf')}>
                    <FilePdfOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                    Export as PDF
                </MenuItem>
            </Menu>
        </Stack>
    )
}


//add list page global actions for mobile
const ListActionMenuItems = () => {
    const theme = useTheme();

    const menuitemsx = { display: 'flex', alignItems: 'center', minHeight: 'auto', p: '8px 16px', '& .anticon': { color: theme.palette.primary.main, mr: '10px' } };
    return (
        <MenuItem
        sx={menuitemsx}
        onClick={async () => {
            try {
                const res = await orderRedirect();

                // open in new tab
                window.open(res.data.url, '_blank');

            } catch (error) {
                console.error(error);
            }
        }}
    >
        <PlusCircleOutlined /> Add Order
    </MenuItem>
    )
}

/**
 * Get props details data for cells.
 */
const getOrderPropsData = (props) => {
    const orderId = props.id;
    const events = { 
        update_init: EVENT_ORDER_UPDATE_INIT, 
        delete_init: EVENT_ORDER_DELETE_INIT,
    }

    const actions = {
        detail: { 
            tooltip: 'View Order', 
            menu: 'View Details', 
            icon: <EyeOutlined />, 
            link: `/orders/${orderId}`,
        },
    }
    const propdetails = { actions: actions, events: events }
    return propdetails;
}

// Mobile column configuration
const mobile_cols = [
    { field: 'order_number', headerName: 'Detail', flex: 1, renderCell: (props) => (<CellMobileDetails item={props.row} propsDetails={getOrderPropsData(props.row)} />) }
]

const AdminOrderButton = () => {
    const { checkPermission } = useAuth();

    const canCreate = checkPermission('order.create');
    
    if (!canCreate) {
        return null;
    }

    const handleCreateOrder = async () => {
        try {
            // Call API to generate signed URL
            const response = await generateUrl();
            
            if (response.success && response.data?.url) {   
                // Open in new window/tab
                window.open(response.data.url, '_blank', 'noopener,noreferrer');
            } else {
                alert('Failed to generate order URL. Please try again.');
            }
            
        } catch (error) {
            
            if (error.response?.status === 403) {
                alert('You do not have permission to create orders for customers.');
            } else {
                alert('Failed to initiate order creation. Please try again.');
            }
        }
    };

    return (
        <Button 
            variant="contained" 
            onClick={handleCreateOrder}
            endIcon={<PlusCircleOutlined />}
        >
            Add Order
        </Button>
    );
};

/**
 * Status Toggle Cell for Orders
 */
const CellStatus = ({ item, onStatusChange }) => {
    const [loading, setLoading] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(item.status);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showDeliveredDialog, setShowDeliveredDialog] = useState(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // Available statuses
    const statusOptions = [
        { key: 'pending', label: 'Pending' },
        { key: 'processing', label: 'Processing' },
        { key: 'confirmed', label: 'Confirmed' },
        { key: 'cancelled', label: 'Cancelled' },
        { key: 'delivered', label: 'Delivered' }
    ];

    useEffect(() => {
        setCurrentStatus(item.status);
    }, [item.status]);

    const handleChipClick = (e) => {
        e.stopPropagation();
        if (loading) return;

        // Don't open menu if status is cancelled or delivered
        if (currentStatus === 'cancelled' || currentStatus === 'delivered') {
            return;
        }

        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectStatus = async (newStatusKey) => {
        handleClose();
        
        // Show confirmation dialog for cancelled status
        if (newStatusKey === 'cancelled') {
            setShowCancelDialog(true);
            return;
        }

        // Show confirmation dialog for delivered status
        if (newStatusKey === 'delivered') {
            setShowDeliveredDialog(true);
            return;
        }

        await updateStatus(newStatusKey);
    };

    const updateStatus = async (newStatusKey) => {
        if (loading) return;

        try {
            setLoading(true);

            const response = await orderToggleStatus(item.id, {
                status: newStatusKey
            });
            const newStatus = response?.data?.status ?? newStatusKey;

            const updatedOrder = {
                ...item,
                status: newStatus,
            };

            setCurrentStatus(newStatus);

            if (onStatusChange) {
                onStatusChange(updatedOrder);
            }

            // Fire event for list refresh
            const eventbus = useEventBusContext();
            eventbus.fire(EVENT_ORDER_UPDATE_SUCCESS, updatedOrder);

        } catch (error) {
            console.error('Failed to update order status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelConfirm = async () => {
        setShowCancelDialog(false);
        await updateStatus('cancelled');
    };

    const handleDeliveredConfirm = async () => {
        setShowDeliveredDialog(false);
        await updateStatus('delivered');
    };

    const getStatusStyles = (status) => {
        switch (String(status).toLowerCase()) {
            case 'pending': return { bgColor: '#fff1b8', fgColor: '#d48806', icon: <AccessTimeOutlined /> };
            case 'processing': return { bgColor: '#e6f7ff', fgColor: '#096dd9', icon: <SyncOutlined /> };
            case 'confirmed': return { bgColor: '#f6ffed', fgColor: '#389e0d', icon: <CheckCircleOutlineRounded /> };
            case 'cancelled': return { bgColor: '#f9e3e1', fgColor: '#cf1322', icon: <CloseCircleOutlined /> };
            case 'delivered': return { bgColor: '#e2ffc8', fgColor: '#237804', icon: <LocalShippingOutlined /> };
            default: return { bgColor: '#f5f5f5', fgColor: '#595959', icon: <InfoOutlined /> };
        }
    };

    const { bgColor, fgColor, icon } = getStatusStyles(currentStatus);

    const label = statusOptions.find(opt => opt.key === currentStatus)?.label || currentStatus;

    // Check if status is final (cancelled or delivered)
    const isFinalStatus = currentStatus === 'cancelled' || currentStatus === 'delivered';

    return (
        <>
            <Chip
                label={label}
                size="medium"
                icon={icon}
                onClick={handleChipClick}
                disabled={loading || isFinalStatus}
                sx={{
                    mt: '5px',
                    color: fgColor,
                    fontSize: '15px',
                    backgroundColor: bgColor,
                    cursor: loading ? 'wait' : (isFinalStatus ? 'not-allowed' : 'pointer'),
                    opacity: '1 !important',
                    transition: 'all 0.2s ease',
                }}
            />

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
            >
                {statusOptions.map((opt) => (
                    <MenuItem
                        key={opt.key}
                        onClick={() => handleSelectStatus(opt.key)}
                        selected={opt.key === currentStatus}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* Cancel Confirmation Dialog */}
            <Dialog 
                open={showCancelDialog} 
                onClose={() => setShowCancelDialog(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogContent dividers sx={{ px: 4, py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <WarningOutlined style={{ fontSize: '48px', color: '#cf1322' }} />
                        <Typography variant="h5" textAlign="center">
                            Are you sure you want to cancel this order?
                        </Typography>
                        <Alert severity="warning" sx={{ width: '100%' }}>
                            This action cannot be undone. The order status will be set to cancelled.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button 
                        onClick={() => setShowCancelDialog(false)} 
                        variant="outlined" 
                        color="secondary"
                    >
                        No, Keep Order
                    </Button>
                    <Button 
                        onClick={handleCancelConfirm} 
                        variant="contained" 
                        color="error"
                        disabled={loading}
                    >
                        {loading ? 'Cancelling...' : 'Yes, Cancel Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delivered Confirmation Dialog */}
            <Dialog 
                open={showDeliveredDialog} 
                onClose={() => setShowDeliveredDialog(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogContent dividers sx={{ px: 4, py: 4 }}>
                    <Stack spacing={2} alignItems="center">
                        <LocalShippingOutlined style={{ fontSize: '48px', color: '#237804' }} />
                        <Typography variant="h5" textAlign="center">
                            Mark this order as delivered?
                        </Typography>
                        <Alert severity="info" sx={{ width: '100%' }}>
                            This will mark the order as completed. This action cannot be undone.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 1.5 }}>
                    <Button 
                        onClick={() => setShowDeliveredDialog(false)} 
                        variant="outlined" 
                        color="secondary"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeliveredConfirm} 
                        variant="contained" 
                        color="success"
                        disabled={loading}
                    >
                        {loading ? 'Updating...' : 'Yes, Mark as Delivered'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const CellTimeDisplay = ({ item }) => {
    
    const time = item.preparation_time_id ? item.preparationtime.time : (item.custom_time ? item.custom_time : null)
    const { from, to } = parseTimeRange(time)

    if (!from) {
        return (
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>—</Typography>
        )
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {from}
            </Typography>
            {to && (
                <>
                    <Typography variant="h6" color="textSecondary">–</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {to}
                    </Typography>
                </>
            )}
        </Box>
    )
}

/**
 * Format currency
 */
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';

    return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <img src={DirhamIcon} width={16} alt="AED" />
            {parseFloat(amount).toFixed(2)}
        </span>
    );
};

const CellPrintAction = ({ item }) => {
    const [open, setOpen] = useState(false)

    return (
        <>
            <Tooltip title="Print">
                <IconButton size="small" color="primary" onClick={() => setOpen(true)}>
                    <PrinterOutlined />
                </IconButton>
            </Tooltip>

            <Dialog open={open} scroll="body" maxWidth="sm" fullWidth onClose={() => setOpen(false)}>
                {open && <OrderPrintDialog order={item} onClose={() => setOpen(false)} />}
            </Dialog>
        </>
    )
}

// Desktop column configuration
const columns = [
    { field: 'order_number', headerName: 'Order #', flex: 0.8, minWidth: 120, renderCell: (props) => (<CellRenderer type="text" item={props.row} field="order_number" propsDetails={getOrderPropsData(props.row)} />) },
    { field: 'customer_name', headerName: 'Customer', flex: 1, minWidth: 150, maxWidth: 150, renderCell: (props) => (<Typography variant="h4" sx={{fontSize: '16px', fontWeight: '400'}}>{props.row.customer?.name || '—'}</Typography>) },
    { field: 'whatsapp_number', headerName: 'WhatsApp', flex: 1, minWidth: 170, renderCell: (props) => (<Typography variant="h4" sx={{fontSize: '16px', fontWeight: '400'}} >{formatPhoneNumber(props.row.customer?.whatsapp_number) || '—'}</Typography>) },
    { field: 'order_date', headerName: 'Order Date', flex: 0.8, minWidth: 130, maxWidth: 130, renderCell: (props) => (<CellRenderer type="date" item={props.row} field="order_date" />) },
    { field: 'delivery_date', headerName: 'Delivery Date', flex: 0.8, minWidth: 120, renderCell: (props) => (<CellRenderer type="date" item={props.row} field="delivery_date" />) },
    { field: 'preparation_time', headerName: 'Preferred Time', flex: 0.7, minWidth: 180, renderCell: (props) => (<CellTimeDisplay item={props.row} />) },
    { field: 'total', headerName: 'Total', flex: 0.7, minWidth: 130, renderCell: (props) => (<Typography variant="h6" fontWeight={600}>{formatCurrency(props.row.total) || '—'}</Typography>) },
    { field: 'status', headerName: 'Status', flex: 0.7,  minWidth: 150, maxWidth: 150, renderCell: (props) => (<CellStatus item={props.row} onStatusChange={() => {}} />) },
    { field: 'action', headerName: 'Action', sortable: false, flex: 0.5, minWidth: 120, renderCell: (props) => ( <Stack direction="row" spacing={0.5}> <CellRenderer type="action" item={props.row} propsDetails={getOrderPropsData(props.row)} /><CellPrintAction item={props.row} /> </Stack>)},
]

export default OrderList